import { apiConfig, http } from "@api/base";
import { add as addRelation, remove as removeRelation } from "@api/relations";
import { getSearchTotal } from "@api/utils";
import { specialCollectionSerializer as serializer } from "./serializer";

const specialCollectionURL = "/specialCollection/";

const get = async (specialCollectionPid) => {
  const response = await http.get(
    `${specialCollectionURL}${specialCollectionPid}`
  );
  response.data = serializer.fromJSON(response.data);
  return response;
};

const create = async (data) => {
  const resp = await http.post(`${specialCollectionURL}`, data);
  resp.data = serializer.fromJSON(resp.data);
  return resp;
};

const update = async (collectionPid, data) => {
  const response = await http.put(
    `${specialCollectionURL}${collectionPid}`,
    data
  );
  response.data = serializer.fromJSON(response.data);
  return response;
};

const del = async (collectionPid) => {
  return await http.delete(`${specialCollectionURL}${collectionPid}`);
};

const viewEvent = async (collectionPid) => {
  return await http.post(`${specialCollectionURL}${collectionPid}/stats`, {
    event: "record-view",
  });
};

const createRelation = async (
  referrer,
  relatedList,
  relationType,
  extra = {}
) => {
  const url = `${specialCollectionURL}${referrer.metadata.pid}`;
  const resp = await addRelation(
    url,
    referrer,
    relatedList,
    relationType,
    extra
  ); // Added await
  resp.data = serializer.fromJSON(resp.data);
  return resp;
};

const deleteRelation = async (referrer, relation) => {
  const url = `${specialCollectionURL}${referrer.metadata.pid}`;
  const relationType = relation.relation_type;
  const resp = await removeRelation(url, referrer, relation, relationType); // Added await
  resp.data = serializer.fromJSON(resp.data);
  return resp;
};

class QueryBuilder {
  constructor() {
    this.withDegreeTypeQuery = [];
    this.withDepartmentQuery = [];
    this.withYearQuery = [];
    this.withAdvisorQuery = [];
    this.withAvailabilityQuery = [];
    this.withTagQuery = [];
    this.size = "";
    this.withEitemsQuery = [];
    this.withSubjectsQuery = [];
    this.sortByQuery = "";
    this.sortOrderQuery = "";
  }

  withDegreeType(degreeType) {
    if (!degreeType) {
      throw TypeError("Degree type argument missing");
    }
    this.withDegreeTypeQuery.push(`degree_type:"${degreeType}"`);
    return this;
  }

  withDepartment(department) {
    if (!department) {
      throw TypeError("Department argument missing");
    }
    this.withDepartmentQuery.push(`department:"${department}"`);
    return this;
  }

  withYear(year) {
    if (!year) {
      throw TypeError("Year argument missing");
    }
    this.withYearQuery.push(`publication_year:${year}`);
    return this;
  }

  withAdvisor(advisor) {
    if (!advisor) {
      throw TypeError("Advisor argument missing");
    }
    this.withAdvisorQuery.push(`advisor:"${advisor}"`);
    return this;
  }

  withAvailability(status) {
    if (status === "available") {
      this.withAvailabilityQuery.push(
        "availability.copies_available_for_loan_count:>0"
      );
    } else if (status === "unavailable") {
      this.withAvailabilityQuery.push(
        "availability.copies_available_for_loan_count:0"
      );
    }
    return this;
  }

  withTag(tag) {
    if (!tag) {
      throw TypeError("Tag argument missing");
    }
    this.withTagQuery.push(`tags:"${tag}"`);
    return this;
  }

  withSize(size) {
    if (size > 0) this.size = `&size=${size}`;
    return this;
  }

  withEitems() {
    this.withEitemsQuery.push("eitems.total:>0");
    return this;
  }

  withSubjects(subjects) {
    if (subjects === undefined) {
      return this;
    }
    const subjectQuery = subjects.map((subject) => {
      return `(subjects.scheme:"${subject.scheme}" AND subjects.value:"${subject.value}")`;
    });
    if (subjectQuery !== "") {
      this.withSubjectsQuery.push(`(${subjectQuery.join(" OR ")})`);
    }
    return this;
  }

  sortBy(by = "bestmatch") {
    this.sortByQuery = `&sort=${by}`;
    return this;
  }

  sortOrder(order = "asc") {
    this.sortOrderQuery = `&order=${order}`;
    return this;
  }

  qs() {
    const searchCriteria = this.withDegreeTypeQuery
      .concat(
        this.withDepartmentQuery,
        this.withYearQuery,
        this.withAdvisorQuery,
        this.withAvailabilityQuery,
        this.withTagQuery,
        this.withEitemsQuery,
        this.withSubjectsQuery
      )
      .join(" AND ");
    return `${searchCriteria}${this.sortByQuery}${this.sortOrderQuery}${this.size}`;
  }
}

const queryBuilder = () => {
  return new QueryBuilder();
};

const list = async (query) => {
  const response = await http.get(`${specialCollectionURL}?q=${query}`);
  response.data.total = getSearchTotal(response.data.hits);
  response.data.hits = response.data.hits.hits.map((hit) =>
    serializer.fromJSON(hit)
  );
  return response;
};

const count = async (query) => {
  const response = await http.get(`${specialCollectionURL}?q=${query}`);
  response.data = getSearchTotal(response.data.hits);
  return response;
};

export const specialCollectionsApi = {
  searchBaseURL: `${apiConfig.baseURL}${specialCollectionURL}`,
  get: get,
  create: create,
  update: update,
  delete: del,
  viewEvent: viewEvent,
  createRelation: createRelation,
  deleteRelation: deleteRelation,
  list: list,
  count: count,
  query: queryBuilder,
  serializer: serializer,
};
